// apps/web/src/widgets/shopping-list-aggregator/aggregate.ts
//
// Phase 11.8 — REND-01 (Strand 1) the PURE per-pass summation helper for the
// shopping-list-aggregator renderer. It turns the recipe's grouped ingredient
// lines + the resolved sizing scale (the selected batch-size position ×
// multiplier) into an ordered set of buckets, each holding accumulated
// line-items keyed by ingredient. It is the FUNCTIONAL contract of the reference
// per-pass model (loop the ingredient groups across the selected passes and SUM
// the scaled amounts) — testable WITHOUT a DOM.
//
// PURITY (mirrors scaling-matrix/compute-scaling.ts + format-amount.ts): no DOM,
// no React, NO console, no Date / Math.random — a plain pure function set. The
// `!Number.isFinite` guard runs at entry AND after every arithmetic op; a
// non-finite scale or amount degrades the line (its amount becomes `undefined`)
// instead of propagating NaN/Infinity. The renderer owns the "—" fallback for an
// `undefined` amount.
//
// GENERALITY (C1 — scripts/grep-no-recipe-identifiers.sh): this module holds
// ONLY the generic summation shape. It names NO unit, NO category, NO recipe
// vocabulary, and adds NO field to Ingredient. The bucket key is the
// recipe-declared group label as-is; the renderer resolves it to a display label
// via the REUSED resolveGroupLabel. Every value flows from recipe DATA passed in.
//
// isolatedDeclarations (D-15): every export carries an explicit interface +
// return type so tsc emits declarations without crossing module boundaries.

// One amount-bearing ingredient line from the recipe (the subset of the
// Ingredient shape this helper consumes — name + optional numeric amount + an
// optional secondary note for the renderer's inline .v-note). The `note` is
// passed through VERBATIM (recipe DATA); it is NOT parsed.
export interface AggregateLineInput {
  readonly name: string;
  readonly amount: number | undefined;
  readonly note: string | undefined;
}

// One recipe-declared bucket of ingredient lines (an ingredient group / category
// membership). `bucketKey` is the group's declared key as-is (the renderer
// resolves it to a display label). `flat` collapses every bucket into one when
// the config requests an ungrouped list.
export interface AggregateGroupInput {
  readonly bucketKey: string;
  readonly lines: ReadonlyArray<AggregateLineInput>;
}

// An accumulated line-item: the ingredient name, the summed amount across the
// selected passes (scaled), and the pass-through secondary note. `amount` is
// `undefined` when the sum is non-finite (the renderer shows "—").
export interface AggregatedLine {
  readonly name: string;
  readonly amount: number | undefined;
  readonly note: string | undefined;
}

// One output bucket: the bucket key (for resolveGroupLabel) + its accumulated
// line-items in first-appearance order.
export interface AggregatedBucket {
  readonly bucketKey: string;
  readonly lines: ReadonlyArray<AggregatedLine>;
}

export interface AggregateArgs {
  // The recipe-declared ingredient groups (the amount-bearing lines, bucketed).
  readonly groups: ReadonlyArray<AggregateGroupInput>;
  // How many selected passes contribute (e.g. the number of selected options).
  // Each selected pass contributes one copy of every line (the reference
  // per-pass model). A count of 0 → every line sums to 0 contributions.
  readonly passCount: number;
  // The resolved per-pass scale (the selected batch-size step multiplier ×
  // the typed multiplier). A non-finite scale degrades each line to `undefined`.
  readonly scale: number;
  // When true, every bucket collapses into a single flat bucket (grouping:"none").
  readonly flat: boolean;
  // The bucket key to use for the single flat bucket (a generic marker the
  // renderer maps through resolveGroupLabel to "" — passed in, never a literal).
  readonly flatBucketKey: string;
}

/** Scale one line's amount by (passCount × scale). Returns `undefined` on any
 *  non-finite input or intermediate so the renderer renders "—" rather than NaN.
 *  An absent amount (the recipe line has no numeric quantity) stays `undefined`. */
function scaledAmount(
  amount: number | undefined,
  passCount: number,
  scale: number,
): number | undefined {
  if (amount === undefined) return undefined;
  if (!Number.isFinite(amount) || !Number.isFinite(passCount) || !Number.isFinite(scale)) {
    return undefined;
  }
  const perPass = amount * scale;
  if (!Number.isFinite(perPass)) return undefined;
  const total = perPass * passCount;
  if (!Number.isFinite(total)) return undefined;
  return total;
}

/** Add two optional amounts. `undefined` is absorbing in the additive sense only
 *  when BOTH sides are absent; a present side carries through. A non-finite sum
 *  degrades to `undefined`. */
function addAmount(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  const sum = a + b;
  if (!Number.isFinite(sum)) return undefined;
  return sum;
}

/**
 * Aggregate the recipe's grouped ingredient lines across the selected passes
 * into ordered buckets of accumulated line-items.
 *
 * - Each selected pass contributes one scaled copy of every line; copies of the
 *   SAME ingredient (same name within a bucket) accumulate into one line-item.
 * - Buckets preserve declaration order; line-items preserve first-appearance
 *   order within a bucket.
 * - `flat` collapses every line into a single bucket (grouping:"none").
 * - A non-finite scale / amount yields an `undefined` line amount (renderer → "—").
 */
export function aggregate(args: AggregateArgs): ReadonlyArray<AggregatedBucket> {
  const { groups, passCount, scale, flat, flatBucketKey } = args;

  // Bucket key → its ordered line list + a name→index map for accumulation.
  const bucketOrder: string[] = [];
  const buckets = new Map<string, { lines: AggregatedLine[]; index: Map<string, number> }>();

  const bucketFor = (key: string): { lines: AggregatedLine[]; index: Map<string, number> } => {
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      bucket = { lines: [], index: new Map<string, number>() };
      buckets.set(key, bucket);
      bucketOrder.push(key);
    }
    return bucket;
  };

  for (const group of groups) {
    const key = flat ? flatBucketKey : group.bucketKey;
    const bucket = bucketFor(key);
    for (const line of group.lines) {
      const contribution = scaledAmount(line.amount, passCount, scale);
      const existingIdx = bucket.index.get(line.name);
      if (existingIdx === undefined) {
        bucket.index.set(line.name, bucket.lines.length);
        bucket.lines.push({ name: line.name, amount: contribution, note: line.note });
      } else {
        const prev = bucket.lines[existingIdx];
        if (prev !== undefined) {
          bucket.lines[existingIdx] = {
            name: prev.name,
            amount: addAmount(prev.amount, contribution),
            // Preserve the first non-empty note encountered for the ingredient.
            note: prev.note ?? line.note,
          };
        }
      }
    }
  }

  return bucketOrder.map((key) => {
    const bucket = buckets.get(key);
    return { bucketKey: key, lines: bucket?.lines ?? [] };
  });
}

/** Sum every accumulated amount across every bucket into one optional total —
 *  the "totals block" summed amount for a given bucket key. Returns `undefined`
 *  when no finite amount contributes (renderer → "—"). */
export function sumBucket(
  buckets: ReadonlyArray<AggregatedBucket>,
  bucketKey: string,
): number | undefined {
  let total: number | undefined;
  for (const bucket of buckets) {
    if (bucket.bucketKey !== bucketKey) continue;
    for (const line of bucket.lines) {
      total = addAmount(total, line.amount);
    }
  }
  return total;
}
